/**
 * Resolve Conflicts Modal
 * Shown when booking dates are changed and some inventory items
 * conflict with other bookings in the new timeframe.
 * Lets the user pick alternative inventory items for each conflict.
 */

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, Loader2, AlertTriangle, Check, Package, Clock, Search } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { insertAuditLogBatch } from '../services/bookingAuditLog.service';
import { formatAllocationFailure, type AllocationRpcResult } from '../lib/allocationRpc';

interface ConflictingItem {
  bookingItemId: string;
  currentInventoryItemId: string;
  productTypeId: string;
  productName: string;
  currentSize: string | null;
  currentItemNumber: string | null;
}

interface AlternativeItem {
  id: string;
  nr: number;
  item_number: string;
  combination_code: string | null;
  size: string | null;
  active: boolean | null;
  status: 'available' | 'buffer_conflict';
  isIdentical: boolean;
}

interface Resolution {
  bookingItemId: string;
  newInventoryItemId: string;
  newItemData: {
    nr: number | null;
    item_number: string | null;
    combination_code: string | null;
    size: string | null;
  };
  skipBufferCheck: boolean;
}

interface ResolveConflictsModalProps {
  isOpen: boolean;
  onClose: () => void;
  conflictingItems: ConflictingItem[];
  bookingId: string;
  locationId: string;
  merchantId: string;
  startDate: string;
  endDate: string;
  onResolved: (resolutions: Resolution[]) => void;
}

export default function ResolveConflictsModal({
  isOpen,
  onClose,
  conflictingItems,
  bookingId,
  locationId: _locationId,
  merchantId,
  startDate,
  endDate,
  onResolved
}: ResolveConflictsModalProps) {
  const { t } = useTranslation();
  const { user, profile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Map from bookingItemId → list of alternatives
  const [alternativesMap, setAlternativesMap] = useState<Map<string, AlternativeItem[]>>(new Map());
  // Map from bookingItemId → selected alternative inventory item id
  const [selections, setSelections] = useState<Map<string, string>>(new Map());
  // Map from bookingItemId → whether user chose to ignore buffer
  const [bufferIgnore, setBufferIgnore] = useState<Map<string, boolean>>(new Map());
  // Search queries per item
  const [searchQueries, setSearchQueries] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    if (isOpen && conflictingItems.length > 0) {
      loadAlternatives();
      setSelections(new Map());
      setBufferIgnore(new Map());
      setSearchQueries(new Map());
      setError(null);
    }
  }, [isOpen, conflictingItems]);

  async function loadAlternatives() {
    setLoading(true);
    setError(null);

    try {
      // Get unique product type IDs
      const productTypeIds = [...new Set(conflictingItems.map(c => c.productTypeId))];

      // For each product type, fetch all inventory items
      const itemsByType = new Map<string, Array<{ id: string; nr: number; item_number: string; combination_code: string | null; size: string | null; active: boolean | null }>>();

      for (const ptId of productTypeIds) {
        const { data: items, error: itemsError } = await supabase
          .from('inventory_items')
          .select('id, nr, item_number, combination_code, size, active')
          .eq('product_type_id', ptId)
          .order('nr', { ascending: true });

        if (itemsError) throw itemsError;
        itemsByType.set(ptId, items || []);
      }

      // Every inventory item already assigned to ANY line item of THIS booking.
      // A physical item can't be used twice in the same booking, so none of
      // these may be offered as an alternative — otherwise the swap would put
      // the same item on two overlapping line items and the write would fail
      // with an exclusion violation. allocation_conflicts can't catch this on
      // its own: the booking's new dates aren't persisted yet, so its still-old
      // dates may not overlap the new window.
      const { data: bookingItemRows, error: bookingItemsError } = await supabase
        .from('booking_items')
        .select('inventory_item_id')
        .eq('booking_id', bookingId)
        .not('inventory_item_id', 'is', null);

      if (bookingItemsError) throw bookingItemsError;

      const assignedInBooking = new Set(
        (bookingItemRows || []).map((r: { inventory_item_id: string | null }) => r.inventory_item_id)
      );

      // Build alternatives for each conflicting booking item. Conflicts are
      // classified by the SAME predicate the apply step uses
      // (inventory_items_conflicts → allocation_conflicts), so the badges match
      // the apply outcome — including maintenance buffers and inventory blocking
      // times, which the old client-side check ignored.
      const newAlternativesMap = new Map<string, AlternativeItem[]>();
      const unresolvedItems: string[] = [];

      for (const conflicting of conflictingItems) {
        const typeItems = itemsByType.get(conflicting.productTypeId) || [];

        // Candidate items: active and not already assigned to any line item of
        // this booking (which also excludes the current item being resolved).
        const candidates = typeItems.filter(
          item =>
            item.active &&
            !assignedInBooking.has(item.id)
        );

        // Authoritative conflict classification for the NEW timeframe.
        const kindsByItem = new Map<string, Set<string>>();
        if (candidates.length > 0) {
          const { data: confRows, error: confError } = await (supabase.rpc as any)('inventory_items_conflicts', {
            p_item_ids: candidates.map(i => i.id),
            p_start: startDate,
            p_end: endDate,
            p_exclude_booking_item_id: conflicting.bookingItemId,
          });
          if (confError) throw confError;
          for (const row of (confRows || []) as Array<{ inventory_item_id: string; kind: string }>) {
            if (!kindsByItem.has(row.inventory_item_id)) {
              kindsByItem.set(row.inventory_item_id, new Set());
            }
            kindsByItem.get(row.inventory_item_id)!.add(row.kind);
          }
        }

        const alternatives: AlternativeItem[] = [];

        for (const item of candidates) {
          const kinds = kindsByItem.get(item.id);

          // A direct booking overlap or a blocking-time overlap can never be
          // applied (skip-buffer only bypasses maintenance buffers) — exclude.
          if (kinds && (kinds.has('booking_direct') || kinds.has('blocking_time'))) {
            continue;
          }

          const isIdentical = item.size === conflicting.currentSize;
          const bufferOnly = !!kinds && kinds.has('booking_buffer');

          alternatives.push({
            ...item,
            status: bufferOnly ? 'buffer_conflict' : 'available',
            isIdentical,
          });
        }

        // Sort: identical first (available identical, then buffer identical), then available, then buffer
        alternatives.sort((a, b) => {
          // Primary: identical items first
          if (a.isIdentical && !b.isIdentical) return -1;
          if (!a.isIdentical && b.isIdentical) return 1;
          // Secondary: available before buffer_conflict
          if (a.status === 'available' && b.status === 'buffer_conflict') return -1;
          if (a.status === 'buffer_conflict' && b.status === 'available') return 1;
          // Tertiary: by nr
          return (a.nr || 0) - (b.nr || 0);
        });

        if (alternatives.length === 0) {
          unresolvedItems.push(conflicting.currentItemNumber || conflicting.productName);
        }

        newAlternativesMap.set(conflicting.bookingItemId, alternatives);
      }

      if (unresolvedItems.length > 0) {
        setError(
          t('resolveConflicts.cannotChangeDates', { items: unresolvedItems.join(', ') })
        );
      }

      setAlternativesMap(newAlternativesMap);
    } catch (err) {
      console.error('Error loading alternatives:', err);
      setError(t('resolveConflicts.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }

  function handleSelect(bookingItemId: string, inventoryItemId: string) {
    setSelections(prev => new Map(prev).set(bookingItemId, inventoryItemId));
    // Reset buffer ignore when selection changes
    setBufferIgnore(prev => {
      const next = new Map(prev);
      next.delete(bookingItemId);
      return next;
    });
  }

  function handleBufferIgnoreToggle(bookingItemId: string) {
    setBufferIgnore(prev => {
      const next = new Map(prev);
      next.set(bookingItemId, !prev.get(bookingItemId));
      return next;
    });
  }

  function getSelectedItem(bookingItemId: string): AlternativeItem | undefined {
    const selectedId = selections.get(bookingItemId);
    if (!selectedId) return undefined;
    return alternativesMap.get(bookingItemId)?.find(i => i.id === selectedId);
  }

  // Check if all conflicts are resolved
  const allResolved = conflictingItems.every(c => {
    const selected = getSelectedItem(c.bookingItemId);
    if (!selected) return false;
    // If buffer conflict, user must explicitly acknowledge
    if (selected.status === 'buffer_conflict' && !bufferIgnore.get(c.bookingItemId)) return false;
    return true;
  });

  // Check if any item has zero alternatives (unresolvable)
  const hasUnresolvable = conflictingItems.some(c => {
    const alts = alternativesMap.get(c.bookingItemId);
    return !alts || alts.length === 0;
  });

  async function handleConfirm() {
    if (!allResolved) return;

    setSaving(true);
    setError(null);

    try {
      const resolutions: Resolution[] = [];

      for (const conflicting of conflictingItems) {
        const selectedId = selections.get(conflicting.bookingItemId)!;
        const selectedItem = getSelectedItem(conflicting.bookingItemId)!;
        const skipBuffer = selectedItem.status === 'buffer_conflict';

        resolutions.push({
          bookingItemId: conflicting.bookingItemId,
          newInventoryItemId: selectedId,
          newItemData: {
            nr: selectedItem.nr,
            item_number: selectedItem.item_number,
            combination_code: selectedItem.combination_code,
            size: selectedItem.size,
          },
          skipBufferCheck: skipBuffer,
        });
      }

      // Apply the date change and all reassignments in ONE transactional RPC.
      // It persists the new dates, then the item swaps, validating each against
      // the NEW timeframe — so the apply-time check matches what this modal
      // showed. All-or-nothing; nothing is written if any item still conflicts.
      const { data: rpcData, error: rpcError } = await (supabase.rpc as any)('apply_booking_date_change', {
        p_booking_id: bookingId,
        p_new_start: startDate,
        p_new_end: endDate,
        p_reassignments: resolutions.map(r => ({
          booking_item_id: r.bookingItemId,
          new_inventory_item_id: r.newInventoryItemId,
          skip_buffer_check: r.skipBufferCheck,
        })),
      });
      if (rpcError) throw rpcError;
      const result = rpcData as AllocationRpcResult | null;
      if (!result?.ok) {
        // Surface the specific reason (item blocked / already booked by …)
        // rather than the generic catch-all below.
        setError(result ? formatAllocationFailure(result, t) : t('changeAllocation.failedToChange'));
        setSaving(false);
        return;
      }

      // Log each reallocation to the audit log (fire-and-forget)
      const auditEntries = resolutions.map(r => {
        const conflicting = conflictingItems.find(c => c.bookingItemId === r.bookingItemId);
        return {
          bookingId,
          merchantId,
          changeType: 'inventory_reallocated',
          changeDetails: {
            product_name: conflicting?.productName || 'Unknown',
            from_item: conflicting?.currentItemNumber || null,
            to_item: r.newItemData.item_number || (r.newItemData.nr ? `#${r.newItemData.nr}` : null),
            reason: 'date_change_conflict',
          },
          userId: user?.id,
          userEmail: profile?.full_name || user?.email || null,
          triggeredBy: 'admin_panel',
        };
      });
      if (auditEntries.length > 0) {
        insertAuditLogBatch(auditEntries);
      }

      onResolved(resolutions);
      onClose();
    } catch (err) {
      console.error('Error resolving conflicts:', err);
      const pgErr = err as any;
      if (pgErr?.code === '23P01') {
        setError(pgErr.message || t('resolveConflicts.itemNotAvailable'));
      } else {
        setError(t('resolveConflicts.failedToResolve'));
      }
    } finally {
      setSaving(false);
    }
  }

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 isolate">
      <div
        className="rounded-lg shadow-2xl w-full overflow-hidden flex flex-col mx-auto relative z-50 bg-primary"
        style={{ maxHeight: '90vh', maxWidth: '560px' }}
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-md sm:p-lg border-b border-primary bg-primary">
          <div className="flex items-center gap-md">
            <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-warning flex-shrink-0" />
            <div className="min-w-0">
              <h2 className="text-lg sm:text-xl font-bold text-primary">{t('resolveConflicts.title')}</h2>
              <p className="text-sm text-secondary">
                {t('resolveConflicts.itemsUnavailable', { count: conflictingItems.length })}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="btn-icon flex-shrink-0" title={t('resolveConflicts.close')}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-md sm:p-lg">
          {error && (
            <div className="p-md bg-danger/10 border-l-4 border-danger rounded-lg mb-lg">
              <p className="text-sm text-danger">{error}</p>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-2xl">
              <Loader2 className="w-6 h-6 text-blue animate-spin" />
              <span className="ml-md text-secondary">{t('resolveConflicts.loadingAlternatives')}</span>
            </div>
          ) : (
            <div className="space-y-xl">
              {conflictingItems.map(conflicting => {
                const alternatives = alternativesMap.get(conflicting.bookingItemId) || [];
                const selectedId = selections.get(conflicting.bookingItemId);
                const selectedItem = getSelectedItem(conflicting.bookingItemId);
                const searchQuery = searchQueries.get(conflicting.bookingItemId) || '';
                const filtered = searchQuery.trim()
                  ? alternatives.filter(item =>
                      item.item_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      item.combination_code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      item.nr?.toString().includes(searchQuery) ||
                      item.size?.toLowerCase().includes(searchQuery.toLowerCase())
                    )
                  : alternatives;

                return (
                  <div key={conflicting.bookingItemId} className="border border-primary rounded-lg overflow-hidden">
                    {/* Item header */}
                    <div className="p-md bg-warning/5 border-b border-primary">
                      <div className="flex items-center gap-sm">
                        <Package className="w-4 h-4 text-warning flex-shrink-0" />
                        <span className="font-medium text-primary">
                          {conflicting.productName}
                          {conflicting.currentSize ? ` (${conflicting.currentSize})` : ''}
                        </span>
                        <span className="text-sm text-secondary">
                          — {t('resolveConflicts.currently')} {conflicting.currentItemNumber || t('resolveConflicts.unassigned')}
                        </span>
                        {selectedId && (
                          <span className="ml-auto">
                            <Check className="w-4 h-4 text-success" />
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Alternatives */}
                    <div className="p-md">
                      {alternatives.length === 0 ? (
                        <p className="text-sm text-danger py-sm">
                          {t('resolveConflicts.noAlternatives')}
                        </p>
                      ) : (
                        <>
                          {/* Search (only show if many items) */}
                          {alternatives.length > 5 && (
                            <div className="relative mb-md">
                              <Search className="absolute left-sm top-1/2 -translate-y-1/2 w-4 h-4 text-tertiary" />
                              <input
                                type="text"
                                value={searchQuery}
                                onChange={e => setSearchQueries(prev => new Map(prev).set(conflicting.bookingItemId, e.target.value))}
                                placeholder={t('resolveConflicts.searchItems')}
                                className="input input-sm pl-xl w-full"
                              />
                            </div>
                          )}

                          <div className="space-y-xs" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                            {filtered.map(item => {
                              const isSelected = selectedId === item.id;

                              return (
                                <button
                                  key={item.id}
                                  onClick={() => handleSelect(conflicting.bookingItemId, item.id)}
                                  className={`w-full flex items-center gap-md p-sm rounded-lg border text-left transition-colors ${
                                    isSelected
                                      ? 'border-blue bg-blue/10'
                                      : item.status === 'buffer_conflict'
                                        ? 'border-primary hover:border-warning hover:bg-warning/5'
                                        : 'border-primary hover:border-blue hover:bg-blue/5'
                                  }`}
                                >
                                  {/* Selection indicator */}
                                  <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                                    isSelected ? 'border-blue bg-blue' : 'border-tertiary'
                                  }`}>
                                    {isSelected && <Check className="w-3 h-3 text-white" />}
                                  </div>

                                  {/* Item info */}
                                  <div className="flex-1 min-w-0 flex items-center gap-md flex-wrap">
                                    <span className="font-medium text-primary text-sm">
                                      {item.item_number || `#${item.nr}`}
                                    </span>
                                    {item.combination_code && (
                                      <span className="text-xs text-secondary">
                                        {item.combination_code}
                                      </span>
                                    )}
                                    {item.size && (
                                      <span className="text-xs text-secondary">
                                        {t('changeAllocation.sizeLabel')}: {item.size}
                                      </span>
                                    )}
                                  </div>

                                  {/* Badges */}
                                  <div className="flex items-center gap-xs flex-shrink-0">
                                    {conflicting.currentSize && item.size && item.size === conflicting.currentSize && (
                                      <span className="text-xs px-sm py-0.5 rounded-full bg-success/20 text-success font-medium">
                                        {t('changeAllocation.sameSize')}
                                      </span>
                                    )}
                                    {conflicting.currentSize && item.size && item.size !== conflicting.currentSize && (
                                      <span className="text-xs px-sm py-0.5 rounded-full bg-warning/20 text-warning font-medium">
                                        {t('changeAllocation.differentSize')}
                                      </span>
                                    )}
                                    {item.status === 'buffer_conflict' && (
                                      <span className="badge badge-warning text-xs flex items-center gap-xs">
                                        <Clock className="w-3 h-3" />
                                        {t('changeAllocation.buffer')}
                                      </span>
                                    )}
                                    {item.status === 'available' && (
                                      <span className="badge badge-success text-xs">{t('changeAllocation.available')}</span>
                                    )}
                                  </div>
                                </button>
                              );
                            })}
                          </div>

                          {/* Buffer conflict acknowledgment */}
                          {selectedItem?.status === 'buffer_conflict' && (
                            <div className="mt-md p-sm bg-warning/5 border border-warning/30 rounded-lg">
                              <label className="flex items-start gap-sm cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={bufferIgnore.get(conflicting.bookingItemId) || false}
                                  onChange={() => handleBufferIgnoreToggle(conflicting.bookingItemId)}
                                  className="mt-0.5"
                                />
                                <span className="text-sm text-secondary">
                                  {t('resolveConflicts.bufferConflictAcknowledge')}
                                </span>
                              </label>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-md sm:p-lg border-t border-primary bg-primary">
          <button onClick={onClose} className="btn btn-secondary" disabled={saving}>
            {t('common.cancel')}
          </button>
          <button
            onClick={handleConfirm}
            disabled={!allResolved || saving || hasUnresolvable || loading}
            className="btn btn-primary"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('resolveConflicts.applying')}
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                {t('resolveConflicts.applyChanges')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
